import { ConditionClause, ConfigurationData, ParsedAST, ParsedElementInfo } from '../types';
import { parseConditionExpression } from '../services/api';

/**
 * Converts visual condition clauses to text expression
 * @param clauses Array of condition clauses
 * @param config Configuration data containing element definitions (optional)
 * @returns Generated text expression
 */
export const convertVisualToText = (
  clauses: ConditionClause[],
  config?: ConfigurationData | null
): string => {
  const validClauses = clauses.filter(
    clause =>
      clause.element &&
      clause.operator &&
      clause.value !== undefined &&
      clause.value !== null &&
      String(clause.value).trim() !== ''
  );

  return validClauses
    .map(clause => {
      let valueStr: string;

      // Check if this is a custom signal and determine element name
      const isCustomSignal =
        config?.custom_signals?.[clause.element]?.is_custom_signal || // eslint-disable-line @typescript-eslint/prefer-nullish-coalescing
        config?.elements?.[clause.element]?.is_custom_signal;
      const elementName = isCustomSignal ? `app.customSignal['${clause.element}']` : clause.element;

      // Dot operators that require parentheses and array format
      const dotArrayOperators = [
        '.contains',
        '.exactlyMatches',
        '.notContains',
        '.matches',
        '.inAtLeastOne',
        '.notInAtLeastOne',
        '.inAll',
        '.notInAll',
      ];
      // Regular array operators (non-dot operators)
      const arrayOperators = ['in', 'contains', 'exactlyMatches', 'notContains', 'matches'];

      if (dotArrayOperators.includes(clause.operator)) {
        // Handle dot operators with proper format
        const content = String(clause.value);
        const quotedContent = `'${content.replace(/'/g, "\\'")}'`;
        return `${elementName}${clause.operator}([${quotedContent}])`;
      } else if (arrayOperators.includes(clause.operator)) {
        // Handle array operators - use array format
        const content = String(clause.value);
        if (content.includes(',')) {
          // Multiple values - split, quote each, and join
          const values = content.split(',').map(v => v.trim());
          const quotedValues = values.map(v => `'${v.replace(/'/g, "\\'")}'`);
          valueStr = `[${quotedValues.join(', ')}]`;
        } else {
          // Single value - still use array format
          const quotedContent = `'${content.replace(/'/g, "\\'")}'`;
          valueStr = `[${quotedContent}]`;
        }
      } else {
        // Handle regular operators
        const content = String(clause.value);

        // Check if value is numeric
        const numericValue = parseFloat(content);
        if (!isNaN(numericValue) && isFinite(numericValue) && String(numericValue) === content) {
          valueStr = content; // Use as number without quotes
        } else {
          valueStr = `'${content.replace(/'/g, "\\'")}'`; // Quote string values with single quotes
        }
      }

      return `${elementName} ${clause.operator} ${valueStr}`;
    })
    .join(' && ');
};

/**
 * Converts parsed AST from backend to visual condition clauses
 * @param ast Parsed AST from backend
 * @returns Array of condition clauses
 */
const convertASTToVisual = (ast: ParsedAST, index = 0): ConditionClause[] => {
  const clauses: ConditionClause[] = [];

  if (ast.type === 'logical_and') {
    // Recursively process left and right sides
    if (ast.left) {
      clauses.push(...convertASTToVisual(ast.left, index));
    }
    if (ast.right) {
      clauses.push(...convertASTToVisual(ast.right, index + (ast.left ? 1 : 0)));
    }
  } else {
    // Single clause (comparison, array_operation, method_call, etc.)
    const clause = convertSingleASTToClause(ast, index);
    if (clause) {
      clauses.push(clause);
    }
  }

  return clauses;
};

// Helper function to handle array values
const handleArrayValues = (ast: ParsedAST): string => {
  if (ast.values && ast.values.length > 0) {
    return ast.values.map(v => String(v.value)).join(', ');
  }
  return '';
};

// Helper function to handle single value
const handleSingleValue = (ast: ParsedAST): string => {
  if (ast.value) {
    return String(ast.value.value);
  }
  return '';
};

/**
 * Converts a single AST node to a condition clause
 */
const convertSingleASTToClause = (ast: ParsedAST, index: number): ConditionClause | null => {
  if (!ast.element) {
    return null;
  }

  const element = getElementName(ast.element);
  let operator = ast.operator ?? '==';
  let value = '';

  // Handle different AST types
  switch (ast.type) {
    case 'comparison':
      value = handleSingleValue(ast);
      break;

    case 'array_operation':
    case 'method_call':
      value = handleArrayValues(ast);
      // For method_call, convert operator to dot notation if needed
      if (ast.type === 'method_call' && operator && !operator.startsWith('.')) {
        operator = `.${operator}`;
      }
      break;

    case 'function_call':
      value = handleSingleValue(ast);
      break;

    default:
      // Fallback for unknown types
      value = handleSingleValue(ast) ?? handleArrayValues(ast);
  }

  return {
    id: `clause-${index}`,
    element,
    operator,
    value,
    rawValue: value,
    isValid: true,
  };
};

/**
 * Extracts element name from parsed element info, handling custom signals and user properties
 */
const getElementName = (elementInfo: ParsedElementInfo): string => {
  if (elementInfo.is_custom_signal) {
    // Extract the custom signal name from the full name
    // e.g., "app.customSignal['user.age']" -> "user.age"
    const match = /app\.customSignal\['([^']+)'\]/.exec(elementInfo.full_name);
    return match ? match[1] : elementInfo.name;
  }

  // For user properties, return the full name so the frontend can properly parse it
  if (elementInfo.is_user_property || elementInfo.full_name.includes('app.userProperty[')) {
    return elementInfo.full_name;
  }

  return elementInfo.name;
};

/**
 * Parses text expression to visual condition clauses using backend parsing
 * @param text Text expression to parse
 * @param config Configuration data containing element definitions (optional)
 * @returns Promise that resolves to array of condition clauses
 */
export const parseTextToVisual = async (
  text: string,
  _config?: ConfigurationData | null
): Promise<ConditionClause[]> => {
  if (!text.trim()) return [];

  try {
    // Call the backend parsing API
    const response = await parseConditionExpression({ expression: text });

    if (response.valid && response.ast) {
      // Convert the AST to visual clauses
      return convertASTToVisual(response.ast);
    } else {
      // Return error clause for invalid expressions
      return [
        {
          id: 'clause-0',
          element: '',
          operator: '==',
          value: '',
          rawValue: text,
          isValid: false,
          validationError: response.errors?.join('; ') ?? 'Invalid expression format',
        },
      ];
    }
  } catch (error) {
    console.error('Error parsing expression with backend:', error);

    // Return error clause for backend parsing failures
    return [
      {
        id: 'clause-0',
        element: '',
        operator: '==',
        value: '',
        rawValue: text,
        isValid: false,
        validationError: `Backend parsing failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
      },
    ];
  }
};

/**
 * Synchronous version that returns a Promise for backward compatibility
 * @param text Text expression to parse
 * @param config Configuration data containing element definitions (optional)
 * @returns Promise that resolves to array of condition clauses
 */
export const parseTextToVisualSync = (
  text: string,
  config?: ConfigurationData | null
): Promise<ConditionClause[]> => {
  return parseTextToVisual(text, config);
};
